import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../Firebase';
import { Users, UserCheck, BookOpen, Award, MapPin, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import logo from '../safi1.jpeg';

const JOURS = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];
const COULEURS = {
  'Grammar':            { bg:'#EEF2FF', border:'#818CF8', text:'#3730A3' },
  'Conjugating':        { bg:'#F0FDF4', border:'#4ADE80', text:'#166534' },
  'Reading & Speaking': { bg:'#FFF7ED', border:'#FB923C', text:'#9A3412' },
};

function getWeekDates(offset = 0) {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  return JOURS.map((_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}
function weekKey(dates) { return dates[0].toISOString().slice(0, 10); }

export default function AdminDashboard({ staff }) {
  const [etudiants, setEtudiants] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState({ niveau: '', pays: '', statut: '' });
  const [weekOffset, setWeekOffset] = useState(0);
  const [dates, setDates]           = useState(getWeekDates(0));
  const [planning, setPlanning]     = useState({});

  useEffect(() => {
    const d = getWeekDates(weekOffset);
    setDates(d);
    fetchPlanning(weekKey(d));
  }, [weekOffset]);

  async function fetchPlanning(key) {
    try {
      const snap = await getDoc(doc(db, 'planning', key));
      if (snap.exists()) setPlanning(snap.data().slots || {});
      else setPlanning({});
    } catch(e) { console.error(e); }
  }

  useEffect(() => {
    async function fetchData() {
      try {
        const snap = await getDocs(collection(db, 'etudiants'));
        const data = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(e => !e.role || e.role === 'etudiant');
        setEtudiants(data);
      } catch(e) { console.error(e); }
      setLoading(false);
    }
    fetchData();
  }, []);

  // Stats
  const total   = etudiants.length;
  const actifs  = etudiants.filter(e => e.actif).length;
  const niveaux = {};
  const pays    = {};
  etudiants.forEach(e => {
    niveaux[e.niveau || 'A1'] = (niveaux[e.niveau || 'A1'] || 0) + 1;
    pays[e.pays || '—'] = (pays[e.pays || '—'] || 0) + 1;
  });

  // Filtre
  const filtered = etudiants.filter(e => {
    if (filter.niveau && e.niveau !== filter.niveau) return false;
    if (filter.pays   && e.pays   !== filter.pays)   return false;
    if (filter.statut === 'actif'   && !e.actif)     return false;
    if (filter.statut === 'inactif' && e.actif)      return false;
    return true;
  });

  const NIVEAUX = ['A1','A2','B1','B2','C1','C2'];
  const PAYS    = [...new Set(etudiants.map(e => e.pays).filter(Boolean))];

  const statCards = [
    { label: 'Total étudiants',  value: total,             icon: Users,     color: '#1B2B5E', bg: 'rgba(27,43,94,0.08)' },
    { label: 'Actifs',           value: actifs,            icon: UserCheck, color: '#2f9e44', bg: 'rgba(47,158,68,0.08)' },
    { label: 'Inactifs',         value: total - actifs,    icon: Users,     color: '#e03131', bg: 'rgba(224,49,49,0.08)' },
    { label: 'Niveau A1',        value: niveaux['A1'] || 0, icon: BookOpen, color: '#C9A84C', bg: 'rgba(201,168,76,0.1)' },
    { label: 'Pays représentés', value: Object.keys(pays).length, icon: MapPin, color: '#7048e8', bg: 'rgba(112,72,232,0.08)' },
    { label: 'Niveaux avancés',  value: etudiants.filter(e => ['B1','B2','C1','C2'].includes(e.niveau)).length, icon: Award, color: '#1098ad', bg: 'rgba(16,152,173,0.08)' },
  ];

  if (loading) return <div className="admin-page-loading"><div className="admin-spinner"/></div>;

  return (
    <div className="admin-dashboard">
      {/* Hero — style espace étudiant */}
      <div className="admin-hero">
        <div className="admin-hero__overlay"/>
        <div className="admin-hero__content">
          <p className="admin-hero__eyebrow">✦ Bienvenue sur votre espace</p>
          <h1 className="admin-hero__title">
            Bonjour, <span>{staff?.nom?.split(' ')[0]}</span> 👋
          </h1>
          <p className="admin-hero__sub">
            Vous êtes connecté en tant que <strong>{staff?.role === 'it_manager' ? 'IT Manager' : staff?.role === 'general_manager' ? 'General Manager' : staff?.role}</strong>.
            Gérez les étudiants et suivez la progression de SAFI Academia.
          </p>
        </div>
      </div>

      <div className="admin-dashboard__body">
        {/* Stat cards */}
        <div className="admin-stat-grid" style={{gridTemplateColumns:'repeat(6,1fr)',marginBottom:'2rem'}}>
          {statCards.map((s,i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="admin-stat-card">
                <div className="admin-stat-card__icon" style={{background:s.bg}}>
                  <Icon size={20} color={s.color}/>
                </div>
                <div>
                  <span className="admin-stat-card__val" style={{color:s.color}}>{s.value}</span>
                  <span className="admin-stat-card__label">{s.label}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Répartition rapide */}
        <div className="admin-dashboard__quick" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem',marginBottom:'2rem'}}>
          <div className="admin-chart-box">
            <h3>Par niveau</h3>
            <div className="admin-niveau-bars">
              {NIVEAUX.map(n => (
                <div key={n} className="admin-niveau-bar">
                  <span>{n}</span>
                  <div className="admin-niveau-bar__track">
                    <div className="admin-niveau-bar__fill" style={{width: total > 0 ? `${((niveaux[n]||0)/total)*100}%` : '0%'}}/>
                  </div>
                  <span>{niveaux[n]||0}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="admin-chart-box">
            <h3>Par pays</h3>
            <div className="admin-niveau-bars">
              {Object.entries(pays).map(([p, count]) => (
                <div key={p} className="admin-niveau-bar">
                  <span style={{minWidth:100}}>{p}</span>
                  <div className="admin-niveau-bar__track">
                    <div className="admin-niveau-bar__fill" style={{width: total > 0 ? `${(count/total)*100}%` : '0%', background:'#C9A84C'}}/>
                  </div>
                  <span>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tableau principal */}
        <div className="admin-table-box">
          <div className="admin-table-box__header">
            <h3>Liste des étudiants ({filtered.length})</h3>
            <div style={{display:'flex',gap:'0.5rem',alignItems:'center',flexWrap:'wrap'}}>
              <select className="admin-select" value={filter.niveau} onChange={e => setFilter(p => ({...p,niveau:e.target.value}))}>
                <option value="">Tous les niveaux</option>
                {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <select className="admin-select" value={filter.pays} onChange={e => setFilter(p => ({...p,pays:e.target.value}))}>
                <option value="">Tous les pays</option>
                {PAYS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select className="admin-select" value={filter.statut} onChange={e => setFilter(p => ({...p,statut:e.target.value}))}>
                <option value="">Tous les statuts</option>
                <option value="actif">Actifs</option>
                <option value="inactif">Inactifs</option>
              </select>
            </div>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Étudiant</th>
                <th>Email</th>
                <th>Pays</th>
                <th>Niveau</th>
                <th>Mode</th>
                <th>Inscription</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{textAlign:'center',padding:'2rem',color:'var(--admin-muted)'}}>Aucun étudiant trouvé</td></tr>
              )}
              {filtered.map(e => (
                <tr key={e.id}>
                  <td><strong>{e.nom}</strong></td>
                  <td>{e.email}</td>
                  <td>{e.pays || '—'}</td>
                  <td><span className="admin-badge admin-badge--niveau">{e.niveau || 'A1'}</span></td>
                  <td>{e.presentiel ? 'Présentiel' : 'En ligne'}</td>
                  <td style={{fontSize:'0.8rem',color:'var(--admin-muted)'}}>
                    {e.createdAt ? new Date(e.createdAt.seconds*1000).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td><span className={`admin-badge ${e.actif?'admin-badge--ok':'admin-badge--err'}`}>{e.actif?'Actif':'Inactif'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── PLANNING SEMAINE ── */}
        <div className="admin-table-box" style={{marginTop:'1.5rem'}}>
          <div className="admin-table-box__header">
            <h3><Calendar size={16}/> Planning de la semaine</h3>
            <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
              <button className="admin-btn admin-btn--secondary" style={{padding:'0.35rem 0.6rem'}} onClick={() => setWeekOffset(w => w-1)}>
                <ChevronLeft size={15}/>
              </button>
              <span style={{fontSize:'0.8rem',color:'var(--admin-muted)',fontWeight:600}}>
                {dates[0].toLocaleDateString('fr-FR',{day:'numeric',month:'short'})} — {dates[5].toLocaleDateString('fr-FR',{day:'numeric',month:'short',year:'numeric'})}
              </span>
              <button className="admin-btn admin-btn--secondary" style={{padding:'0.35rem 0.6rem'}} onClick={() => setWeekOffset(w => w+1)}>
                <ChevronRight size={15}/>
              </button>
              <button className="admin-btn admin-btn--secondary" style={{fontSize:'0.78rem'}} onClick={() => setWeekOffset(0)}>
                Cette semaine
              </button>
            </div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table className="admin-table" style={{minWidth:800}}>
              <thead>
                <tr>
                  <th style={{width:120}}>Créneau</th>
                  {dates.map((d, i) => (
                    <th key={i} style={{
                      textAlign:'center',
                      background: d.toDateString() === new Date().toDateString() ? 'rgba(201,168,76,0.12)' : '',
                      color: d.toDateString() === new Date().toDateString() ? 'var(--admin-gold)' : ''
                    }}>
                      <div style={{fontWeight:700}}>{JOURS[i]}</div>
                      <div style={{fontSize:'0.7rem',fontWeight:400,opacity:0.6}}>
                        {d.toLocaleDateString('fr-FR',{day:'numeric',month:'short'})}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {['Matin (8h-12h)','Après-midi (14h-18h)','Soir (18h-20h)'].map((slot, si) => (
                  <tr key={si}>
                    <td style={{fontWeight:600,fontSize:'0.78rem',color:'var(--admin-muted)',whiteSpace:'nowrap',background:'var(--admin-bg)'}}>
                      {slot}
                    </td>
                    {JOURS.map((jour, ji) => {
                      const cell = planning[`${jour}_${slot}`];
                      const c = cell ? COULEURS[cell.module] : null;
                      const isToday = dates[ji].toDateString() === new Date().toDateString();
                      return (
                        <td key={ji} style={{
                          verticalAlign:'top',
                          padding:'0.5rem',
                          background: isToday ? 'rgba(201,168,76,0.04)' : '',
                          minHeight:80
                        }}>
                          {cell && (
                            <div style={{
                              background:c?.bg,
                              borderLeft:`3px solid ${c?.border}`,
                              borderRadius:6,
                              padding:'0.5rem 0.6rem',
                              fontSize:'0.78rem',
                            }}>
                              <div style={{fontWeight:700,color:c?.text}}>{cell.module}</div>
                              <div style={{color:'var(--admin-text)',marginTop:2}}>👤 {cell.trainer}</div>
                              {cell.niveau && <span className="admin-badge admin-badge--niveau" style={{fontSize:'0.6rem',marginTop:4}}>{cell.niveau}</span>}
                              {cell.notes && <div style={{color:'var(--admin-muted)',fontSize:'0.7rem',marginTop:2}}>📍 {cell.notes}</div>}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}